import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Popover } from '@brighterion/br-ui';
import { RoleService } from 'Services';
import { handleOnClickEvent, handleKeyEvent } from 'Utils';
import { useHistory } from 'react-router-dom';
import { Formik } from 'formik';
import { RULE_ACTION_TYPE } from 'Common/enums';
import ButtonGroup from 'Components/ButtonGroup';
import PageHeader from 'Components/PageHeader';
import DeleteRuleModal from 'Components/DeleteRuleModal/DeleteRuleModal';
import { shouldShowRuleTester, shouldDeleteRule } from 'Selectors';
import { trackEvent, EventTypes, EventNames } from '@brighterion/insights';
import { addRuleValidationSchema } from './schema';
import RuleStatement from './RuleStatement';
import AddRuleInfoOverview from './AddRuleInfoOverview';
import RuleTester from './RuleTester';

import styles from './AddRuleForm.css';

type AddFormProps = {
    mode: 'add' | 'edit' | 'read';
    pageHeader: string;
    formRef?: any;
    formInitialValues: NewRuleFormSchema;
    parameterData: UseGetParametersResponse | undefined;
    listData: ListApiGetResponse;
    mutation: any;
    selectedRowId?: string;
    repoId: number;
    disabledInputFields: DisabledInputFields;
    conclusionFlagListData: ConclusionListApiGetResponse[];
    disableDeleteRuleButton?: boolean;
    isNonTemplatedRule?: boolean;
    isRuleDeployed?: boolean;
};

const AddRuleForm = ({
    mode,
    pageHeader,
    formRef,
    parameterData,
    listData,
    formInitialValues,
    mutation,
    selectedRowId,
    repoId,
    disabledInputFields,
    conclusionFlagListData,
    disableDeleteRuleButton,
    isNonTemplatedRule,
    isRuleDeployed
}: AddFormProps) => {
    const { t } = useTranslation();
    const history = useHistory();
    const [isOpen, setIsOpen] = React.useState(false);
    const showDeleteRuleButton = isNonTemplatedRule
        ? mode === 'edit' && RoleService.hasSimpleRuleDeleteAccess && shouldDeleteRule()
        : false;

    const handleCancel = () => {
        if (selectedRowId) {
            // after editing an existing rule
            history.push(`/global-rules?selectedRowId=${selectedRowId}`);
        } else {
            // after creating a new rule
            history.push('/global-rules');
        }
    };

    const handleSave = (formValues: NewRuleFormSchema) => {
        const {
            ruleId,
            group,
            active,
            description,
            actions,
            comment,
            conditions,
            allowOverrides,
            priority,
            hiddenFromTenant
        } = formValues;

        actions.forEach((action) => {
            /** If ACCEPT, REJECT or REVIEW delete value from values */
            if (
                [RULE_ACTION_TYPE.ACCEPT, RULE_ACTION_TYPE.REJECT, RULE_ACTION_TYPE.REVIEW].includes(
                    action.operator as RULE_ACTION_TYPE
                )
            ) {
                action.values = [''];
            }
            /** Convert number values in string[] */
            if (typeof action.values === 'number') {
                action.values = [String(action.values)];
            }
            /** Convert string values in string[] */
            if (typeof action.values === 'string') {
                action.values = [action.values];
            }
        });

        const transformedConditions = conditions.map(
            ({ parameterField, operatorField, valueField, readOnly, conditionId }) => ({
                conditionId,
                fieldName: parameterField.name[0],
                operator: operatorField[0],
                values: typeof valueField !== 'object' ? [String(valueField)] : valueField,
                readOnly
            })
        );

        mutation.mutate({
            ruleId,
            group: parseInt(`${group}`, 10), // done to format data properly from dropdown [string] format
            priority,
            active,
            description,
            actions,
            conditions: transformedConditions,
            comment: { [ruleId]: comment.trim() },
            templateRepositoryId: repoId,
            allowOverrides,
            level: 'ACQUIRER',
            hiddenFromTenant
        });

        if (mode === 'add') {
            trackEvent({
                eventType: EventTypes.track,
                eventName: EventNames.click,
                payload: {
                    clickIdentifier: 'Simple Rule - Add New Simple Rule',
                    text: 'Add New Simple Rule'
                }
            });
        } else if (mode === 'edit') {
            trackEvent({
                eventType: EventTypes.track,
                eventName: EventNames.click,
                payload: {
                    clickIdentifier: 'Global Rules - Edit Rule',
                    text: 'Edit Rule'
                }
            });
        }
    };

    /**
     * @note Disable override if:
     * - The user is a Threshold Editor (only) and edit the Simple Rule, but can only change the Theshold `status`
     */
    if (mode === 'edit' && RoleService.isThresholdEditor() && RoleService.roles.length === 1) {
        disabledInputFields.disableOverrideChange = true;
    }

    return (
        <Formik
            initialValues={formInitialValues}
            onSubmit={(formValues) => handleSave(formValues)}
            validationSchema={addRuleValidationSchema(t)}
            innerRef={formRef}
        >
            {(formikProps) => {
                const { dirty, isValid, isSubmitting, handleSubmit } = formikProps;
                const disableSave = !dirty || !isValid;

                const submitForm = () => {
                    handleSubmit();
                };

                const toggleModal = () => {
                    setIsOpen(!isOpen);
                };

                return (
                    <>
                        <PageHeader>
                            <PageHeader.Left className={styles.titleText}>{pageHeader}</PageHeader.Left>
                            <PageHeader.Right className={styles.rightButtonContainer}>
                                <ButtonGroup direction='row'>
                                    {showDeleteRuleButton && (
                                        <>
                                            <div>
                                                <Popover
                                                    position='top-end'
                                                    theme='light'
                                                    showArrow
                                                    showOnHover={disableDeleteRuleButton}
                                                    isControlled={!disableDeleteRuleButton}
                                                    popperClassName={styles.popper}
                                                    content={t('disableDeleteRuleButtonTooltip')}
                                                    contentClassName={styles.disableDeleteRuleTooltip}
                                                    arrowClassName={styles.disableDeleteRulePopperArrowStyle}
                                                >
                                                    <Button
                                                        className={styles.deleteRuleButton}
                                                        disabled={isSubmitting || disableDeleteRuleButton}
                                                        iconName='delete'
                                                        dataTestId='addForm-delete-btn'
                                                        variant='tertiary'
                                                        onKeyDown={handleKeyEvent(toggleModal)}
                                                        onClick={handleOnClickEvent(toggleModal)}
                                                    >
                                                        {t('Delete Rule')}
                                                    </Button>
                                                </Popover>
                                            </div>

                                            <hr className={styles.separator} />
                                        </>
                                    )}
                                    <Button
                                        disabled={isSubmitting}
                                        dataTestId='addForm-cancel-btn'
                                        variant='tertiary'
                                        size='medium'
                                        onKeyDown={handleKeyEvent(handleCancel)}
                                        onClick={handleOnClickEvent(handleCancel)}
                                    >
                                        {t('Cancel')}
                                    </Button>
                                    <Button
                                        dataTestId='addForm-save-btn'
                                        variant='primary'
                                        size='small'
                                        onClick={handleOnClickEvent(submitForm)}
                                        onKeyDown={handleKeyEvent(submitForm)}
                                        disabled={disableSave || isSubmitting}
                                    >
                                        {isSubmitting ? t('Saving...') : t('Save Rule')}
                                    </Button>
                                </ButtonGroup>
                            </PageHeader.Right>
                        </PageHeader>
                        <AddRuleInfoOverview
                            mode={mode}
                            ruleType='simple'
                            listData={listData.result}
                            selectedRowId={selectedRowId}
                            disabledInputFields={disabledInputFields}
                        />
                        <RuleStatement
                            parameterData={parameterData}
                            listData={listData}
                            disabledInputFields={disabledInputFields}
                            conclusionFlagListData={conclusionFlagListData}
                        />

                        <DeleteRuleModal
                            templateRepositoryId={repoId}
                            ruleId={formInitialValues.ruleId}
                            isOpen={isOpen}
                            closeModal={() => setIsOpen(false)}
                            isRuleDeployed={!!isRuleDeployed}
                        />

                        {shouldShowRuleTester() && (
                            <RuleTester
                                formikProps={formikProps}
                                parameterData={parameterData}
                                listData={listData}
                                conclusionFlagListData={conclusionFlagListData}
                                mode={mode}
                            />
                        )}
                    </>
                );
            }}
        </Formik>
    );
};

export default AddRuleForm;
